import { Box, Chip, Tooltip, Typography } from "@mui/material";
import { ErrorOutline, InfoOutlined } from "@mui/icons-material";
import { CompletenessCheck } from "@/types";
import { SectionCard } from "@/components/common/SectionCard";

// Extraction completeness surface (#5767), split into two low-footprint pieces
// instead of a full-width banner: a compact chip in the review toolbar for
// glance visibility, and a detail card in the sidebar issues panel. Both
// review layouts (single-client DocumentReviewTab, multi-client
// MultiClientReviewPanel) render both.

/** True when the verdict is worth surfacing (failed or unverifiable). */
export const hasCompletenessSignal = (c?: CompletenessCheck): boolean => !!c && c.status !== "passed";

const missingCount = (c: CompletenessCheck): number => c.expected_members - c.loaded_claimants;

/** Chip label for a failed verdict; `missing === 0` avoids the contradictory "0 extra". */
const failedChipLabel = (missing: number): string => {
  if (missing > 0) return `${missing} member${missing === 1 ? "" : "s"} missing`;
  if (missing < 0) return `${-missing} extra member${-missing === 1 ? "" : "s"}`;
  return "Member count mismatch";
};

/**
 * CompletenessChip — compact toolbar chip. `failed` → red (likely data loss);
 * `unverifiable` → amber (extraction couldn't even count sections — the
 * higher-uncertainty state, so it gets its own visible signal, not just the
 * sidebar note). Full detail is on hover so the chip stays terse.
 */
export const CompletenessChip = ({ completeness }: { completeness?: CompletenessCheck }) => {
  if (!hasCompletenessSignal(completeness)) return null;
  const c = completeness!;

  if (c.status === "unverifiable") {
    return (
      <Tooltip title={c.detail || "Extraction completeness could not be verified"}>
        <Chip
          size="small"
          icon={<InfoOutlined sx={{ fontSize: 16 }} />}
          label="Completeness unverified"
          sx={{
            bgcolor: "warning.main",
            color: "warning.contrastText",
            fontWeight: 600,
            "& .MuiChip-icon": { color: "warning.contrastText" },
          }}
        />
      </Tooltip>
    );
  }

  return (
    <Tooltip title={c.detail || "Extraction completeness check failed"}>
      <Chip
        size="small"
        icon={<ErrorOutline sx={{ fontSize: 16 }} />}
        label={failedChipLabel(missingCount(c))}
        sx={{
          bgcolor: "error.main",
          color: "error.contrastText",
          fontWeight: 600,
          "& .MuiChip-icon": { color: "error.contrastText" },
        }}
      />
    </Tooltip>
  );
};

/**
 * CompletenessCard — sidebar detail. `failed` shows the counts + what likely
 * happened + guidance; `unverifiable` shows a quiet note. Renders nothing when
 * passed/absent.
 */
export const CompletenessCard = ({ completeness }: { completeness?: CompletenessCheck }) => {
  if (!hasCompletenessSignal(completeness)) return null;
  const c = completeness!;

  if (c.status === "unverifiable") {
    return (
      <SectionCard title="Completeness" compact>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75 }}>
          <InfoOutlined sx={{ fontSize: 16, color: "info.main", mt: "2px" }} />
          <Typography variant="body2" color="text.secondary">
            Couldn&apos;t verify completeness — the document&apos;s member sections couldn&apos;t be counted against the{" "}
            {c.loaded_claimants} claimant{c.loaded_claimants === 1 ? "" : "s"} loaded. Confirm against the source before
            approving.
          </Typography>
        </Box>
      </SectionCard>
    );
  }

  const missing = missingCount(c);
  const headline =
    missing > 0
      ? `${missing} member${missing === 1 ? "" : "s"} may have been dropped`
      : missing < 0
        ? `${-missing} extra claimant${-missing === 1 ? "" : "s"} loaded`
        : "Member counts don't match";
  const cause =
    missing > 0
      ? "Members may have been dropped during extraction."
      : missing < 0
        ? "Members may have been duplicated during extraction."
        : "The loaded claimant count doesn't match the document.";
  return (
    <SectionCard title="Completeness" compact>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75 }}>
          <ErrorOutline sx={{ fontSize: 16, color: "error.main", mt: "2px" }} />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {headline}
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ pl: "22px" }}>
          Document lists {c.expected_members} member{c.expected_members === 1 ? "" : "s"}; {c.loaded_claimants} claimant
          {c.loaded_claimants === 1 ? "" : "s"} loaded. {cause} Verify against the source before approving.
        </Typography>
      </Box>
    </SectionCard>
  );
};
